/**
 * useSubscription.ts — POST /api/broker/v1/subscriptions hook
 *
 * On trigger: POSTs { service_id, plan_id? } with Bearer JWT — plan_id is sent
 * when the caller chose a plan (the vertical-broker Order flow), and omitted for
 * the plain access request that has no plan choice.
 * Expected response: { status: 'pending' | 'created', message?: string }
 *
 * Design-contract §11: Binding Lifecycle (order variant)
 * Ref: DOPLA-602 — marketplace user subscription flow
 */

import { useState, useCallback } from 'react';

export type SubscriptionState = 'idle' | 'submitting' | 'success' | 'error';

export interface UseSubscriptionResult {
  state: SubscriptionState;
  trigger: () => Promise<void>;
  reset: () => void;
}

export function useSubscription(
  serviceId: string,
  token: string | null,
  planId?: string | null,
): UseSubscriptionResult {
  const [state, setState] = useState<SubscriptionState>('idle');

  const trigger = useCallback(async () => {
    if (!token) {
      setState('error');
      return;
    }
    setState('submitting');
    try {
      const res = await fetch('/api/broker/v1/subscriptions', {
        cache: 'no-store',
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(planId ? { service_id: serviceId, plan_id: planId } : { service_id: serviceId }),
      });
      if (!res.ok) {
        setState('error');
        return;
      }
      setState('success');
    } catch {
      setState('error');
    }
  }, [serviceId, token, planId]);

  const reset = useCallback(() => setState('idle'), []);

  return { state, trigger, reset };
}
